import { type QueryKey, useQuery } from '@tanstack/react-query';
import { apiJson, isMissingRoute } from '../../api/client';
import { apiHas } from '../../api/contract';
import { ApiError } from '../../api/errors';

/*
 * Чи доступна операція модуля поточному користувачу (для `useAccess()` модуля й прихованих розділів):
 * — операції немає в контракті під час збирання або маршруту немає на сервері (модуль не підключено) — false;
 * — 401/403 — false (розділ не показується);
 * — інші помилки (мережа, 5xx) — true: сторінка модуля сама покаже стан помилки з повтором.
 * Поки відповіді немає, повертає false (вимога ModuleDefinition.useAccess).
 * Результат кешується на 10 хв. Ключ краще брати поза префіксом модуля (['probe', 'meetings']): тоді інвалідація
 * модуля подіями потоку не повторює пробу щоразу.
 */
export function useRouteProbe(key: QueryKey, path: string, opts: { enabled?: boolean; operation?: string } = {}) {
  const operation = opts.operation ?? `GET ${path.split('?')[0]}`;
  const known = apiHas(operation);
  const q = useQuery({
    queryKey: key,
    enabled: (opts.enabled ?? true) && known,
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<boolean> => {
      try {
        await apiJson('GET', path);
        return true;
      } catch (e) {
        if (isMissingRoute(e)) return false;
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) return false;
        return true;
      }
    },
  });
  return { available: known && q.data === true, loading: known && q.isLoading };
}
